import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Modal } from 'react-bootstrap';
import AddNewVote from './AddNewVote';
import VotesList from './VotesList';
import { UserVote } from './VoteStorage';
import './Myvote.css'
import VoteTabs from './VoteTabs';
import { scrollToCenter } from 'common/utils/helper';

// Styled Components
const Container = styled.div`
  padding: ${window.innerWidth > 768 ? "1.5rem" : ""};
  max-width: 1200px;
  margin: 0 auto;
  // height: 80vh; /* or 100vh if you want full screen */
  overflow-y: auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: ${window.innerWidth > 768 ? "row" : "column"};
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color:rgb(255, 255, 255);
  margin: 0;
  font-family: 'Poppins', sans-serif;
`;

const AddButton = styled.button`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.875rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
`;

// Custom styling for React Bootstrap Modal to remove border-radius from three sides
const CustomModalStyle = styled.div`
  .modal-content {
    border-radius: 0.75rem 0 0 0 !important; /* Only top-left corner has border-radius */
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    border: none;
  }
  
  .modal-dialog {
    max-width: 500px;
    margin: 2rem auto;
  }
  
  .modal-header {
    border-bottom: 1px solid #e5e7eb;
    border-radius: 0.75rem 0 0 0 !important; /* Match the modal content border-radius */
  }
  
  .modal-body {
    padding: 0; /* Remove default padding to match original design */
  }
  
  @media (max-height: 600px) {
    .modal-dialog {
      margin: 1rem auto;
    }
    
    .modal-content {
      max-height: calc(100vh - 2rem);
    }
  }
`;


type TabKey = 'active' | 'draft';

function MyVotes() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingVote, setEditingVote] = useState<UserVote | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [ActiveTab, setActiveTab] = useState<TabKey>('active');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const handleOpenModal = useCallback(() => {
    setEditingVote(null);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingVote(null);
  }, []);

  const handleEditVote = useCallback((vote: UserVote) => {
    setEditingVote(vote);
    setIsModalOpen(true);
  }, []);

  const handleSaveVote = useCallback(() => {
    setRefreshKey(prev => prev + 1);
    handleCloseModal();
  }, [handleCloseModal]);

  const handleDeleteVote = useCallback((voteId: string) => {
    // Refresh the votes list after deletion
    setRefreshKey(prev => prev + 1);
  }, []);

  const ChangeTabs = useCallback((key: TabKey) => {
    setActiveTab(key);
  }, []);





  return (
    <Container ref={scrollContainerRef}>
      <Header>
        <VoteTabs
          value={ActiveTab}
          onChange={ChangeTabs}
        />
        <AddButton onClick={handleOpenModal}>
          Add New Vote
        </AddButton>
      </Header>

      <VotesList
        key={refreshKey}
        onEditVote={handleEditVote}
        onDeleteVote={handleDeleteVote}
        draftFilter={ActiveTab === "draft"}
      />

      <CustomModalStyle>
        <Modal
          show={isModalOpen}
          onHide={handleCloseModal}
          size="lg"
          centered
          backdrop="static"
          contentClassName="AddVoteCss"
          keyboard={true}
        >
          <div className="d-flex justify-content-end">
            <button type="button" className="btn-close " aria-label="Close" onClick={handleCloseModal}></button>
          </div>

          <AddNewVote
            editingVote={editingVote}
            onSave={handleSaveVote}
            onCancel={handleCloseModal}
          />
        </Modal>
      </CustomModalStyle>
    </Container>
  );
}

export default MyVotes;
